import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { refs, type ContractorGroup } from '@/api/refs';
import { useConfirm } from '@/components/ConfirmProvider';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';

/**
 * Управління групами контрагентів (tenant-scoped, TD-GROUPS B 2026-06-06).
 * Дефолтні 3 групи (is_seed) — не можна видалити. Юзер створює свої.
 * При імпорті з 1С група контрагента мапиться на цей довідник (get-or-create
 * по name). Не плутати з ролями (покупець/постачальник/нерезидент).
 */
export default function ContractorGroupsPage() {
  const confirm = useConfirm();
  const qc = useQueryClient();
  const [newName, setNewName] = useState('');

  const list = useQuery({
    queryKey: ['contractor-groups'],
    queryFn: refs.contractorGroups.list,
  });

  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ['contractor-groups'] });

  const create = useMutation({
    mutationFn: () => refs.contractorGroups.create({ name: newName.trim() }),
    onSuccess: () => { setNewName(''); invalidate(); },
  });
  const update = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      refs.contractorGroups.update(id, { name }),
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: (id: string) => refs.contractorGroups.remove(id),
    onSuccess: invalidate,
  });

  const rows = list.data ?? [];

  const cols = useMemo<ColumnDef<ContractorGroup>[]>(() => [
    {
      // Колонка «Назва»: дефолтна група (is_seed) показується як текст з 🔒,
      // користувацька — як інлайн-input з збереженням on-blur (як було у таблиці).
      key: 'name', header: 'Назва',
      sortValue: (g) => g.name.toLowerCase(),
      cell: (g) =>
        g.is_seed ? (
          <span>
            <span title="Дефолтна група">🔒 </span>
            {g.name}
          </span>
        ) : (
          <input
            defaultValue={g.name}
            onBlur={(e) => {
              const v = e.target.value.trim();
              if (v && v !== g.name) update.mutate({ id: g.id, name: v });
            }}
            style={{ width: '90%' }}
          />
        ),
    },
    {
      key: 'actions', header: '', width: 100, align: 'center',
      cell: (g) =>
        !g.is_seed && (
          <button
            className="danger"
            title="Видалити групу"
            onClick={() =>
              confirm(`Видалити групу «${g.name}»?`).then((ok) => {
                if (ok) remove.mutate(g.id);
              })
            }
          >
            ✕
          </button>
        ),
    },
  ], [confirm, remove, update]);

  return (
    <>
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Групи контрагентів</h2>
      </div>
      <p style={{ color: 'var(--muted)', maxWidth: 720 }}>
        Групи допомагають упорядкувати контрагентів (наприклад, «Покупці»,
        «Постачальники», «VIP-клієнти»). Дефолтні групи позначені 🔒 — їх не
        можна видалити. Це <strong>не те саме</strong>, що ролі контрагента
        (покупець / постачальник / нерезидент) — роль вказується у картці
        контрагента окремо.
      </p>

      {/* Створення нової групи */}
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', margin: '1rem 0' }}>
        <input
          placeholder="Назва нової групи"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          style={{ minWidth: 240 }}
        />
        <button
          className="primary"
          disabled={!newName.trim() || create.isPending}
          onClick={() => create.mutate()}
        >
          + Додати групу
        </button>
      </div>

      <div style={{ maxWidth: 720 }}>
        <DataTable<ContractorGroup>
          rows={rows}
          columns={cols}
          rowKey={(g) => g.id}
          loading={list.isLoading}
          emptyText="Немає груп контрагентів."
          rowMuted={(g) => (g.deletion_mark ? 0.4 : undefined)}
          initialSortKey="name"
        />
      </div>
    </>
  );
}
